import { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { Center, Flex, useDisclosure } from '@chakra-ui/react';

import { SensitiveOperationType } from '__generated__/GQL';
import IconCircle from 'components/_core/IconCircle';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import SMSVerificationModal from 'components/SMSVerificationModal';
import { CreditCard } from 'features/PaymentCards/hooks/useCreditCards';
import useCardSecurityDetails from 'features/PaymentCards/pages/PaymentCardListLegacy/hooks/useCardSecurityDetails';

import { emptyDetails, useCardDetailsContext } from '../../context';
import locales from './locales';

type CardSecurityDetailsProps = {
  creditCard: Pick<CreditCard, 'creditCardId' | 'isPhysical'>;
};

const CardSecurityDetails: FC<CardSecurityDetailsProps> = ({ creditCard }) => {
  const { creditCardId, isPhysical } = creditCard;

  const history = useHistory();

  const [shouldCopy, setShouldCopy] = useState(false);

  const { isOpen, onClose, onOpen } = useDisclosure();

  const {
    copyPANToClipBoard,
    fetchingSecurityDetails,
    getCardSecurityDetails,
  } = useCardSecurityDetails({
    creditCardId,
    shouldCopy,
  });

  const {
    cardDetails: { PAN },
    setCardDetails,
  } = useCardDetailsContext();

  const handleHideOrShowCardDetails = () => {
    if (PAN) {
      setCardDetails(emptyDetails);
      return;
    }
    if (isPhysical) {
      setShouldCopy(false);
      onOpen();
    } else {
      history.push(`/cards/${creditCardId}/show-details`);
    }
  };

  const handleCopyDetails = () => {
    if (PAN) {
      copyPANToClipBoard(PAN);
      return;
    }
    if (isPhysical) {
      setShouldCopy(true);
      onOpen();
    } else {
      history.push(`/cards/${creditCardId}/copy-details`);
    }
  };

  const handleTokenRetrieved = async (token: string) => {
    const success = await getCardSecurityDetails(token);
    if (success) {
      onClose();
    }
  };

  return (
    <>
      <SMSVerificationModal
        actionData={{ creditCardId }}
        actionType={SensitiveOperationType.DisplayCardInformation}
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={handleTokenRetrieved}
      />
      <Center gap="space-16">
        {fetchingSecurityDetails ? (
          <Loader />
        ) : (
          <>
            <Flex
              alignItems="center"
              cursor="pointer"
              gap="space-8"
              onClick={handleHideOrShowCardDetails}
            >
              <IconCircle
                backgroundColor="grey.200"
                color="grey.800"
                name="eye-open"
                size="40px"
              />

              <Text size="sm" variant="primary">
                {PAN ? locales.hideDetails : locales.revealDetails}
              </Text>
            </Flex>

            <Flex
              alignItems="center"
              cursor="pointer"
              gap="space-8"
              onClick={handleCopyDetails}
            >
              <IconCircle
                backgroundColor="grey.200"
                color="grey.800"
                name="copy"
                size="40px"
              />

              <Text size="sm" variant="primary">
                {locales.copy}
              </Text>
            </Flex>
          </>
        )}
      </Center>
    </>
  );
};

export default CardSecurityDetails;
